/* OsCatalogEditor.js */

"use strict";
const GameEditor = require("@gluenetworks/game_editor");
const _ = require("lodash");

/**
 * @param {UserSession} userSession
 * @param {OSCatalogEntity} OSCatalog
 * @param {SolutionsManager} solutionsManager
 * @param {IO} steveIo
 */

class OsCatalogEditor extends GameEditor {
    constructor(
        logger,
        di,
        registeredDetails,
        editorState,
        editorLayoutMgr,
        editorLayout,
        DeviceEntity,
        OsImageCatalog,
        OsImagePaths,
        OsImage,
        userSession,
        userService,
        editorTabCmpnt,
        solutionsManager,
        steveIo
    ) {
        super(logger, di, editorState.editorState, editorLayoutMgr);
        this._logger = logger;
        this._registeredDetails = registeredDetails;
        this._editorLayout = editorLayout;
        this._DeviceEntity = DeviceEntity;
        this._OsCatalogEntity = OsImageCatalog;
        this._OsImagePathsEntity = OsImagePaths;
        this._OsImageEntity = OsImage;
        this._tabCmpnt = editorTabCmpnt;
        this._editorTabState = editorState;
        this._steveIo = steveIo;
        this._userSession = userSession;
        this._userService = userService;
        this._activeOrgId = userSession.activeOrganization.id;
        this._userRBAC = false;
        this._userId = _.get(this._userSession, "user.id", "");

        let solutionDefinition = solutionsManager.getActiveSolution();
        let osCatalogEditorViews = _.get(solutionDefinition, "osCatalogEditor.viewNamespaces", []);
        let osCatalogEditorActions = _.get(
            solutionDefinition,
            "osCatalogEditor.actionNamespaces",
            []
        );
        let launchOptions = editorState.launchOptions;

        this.setNamespaceGlobs(osCatalogEditorViews.concat(osCatalogEditorActions), true);

        this.currentEntityRemoved = false;
        this.currentEntityDeleteInProgress = false;

        this._osCatalog = undefined;
        this._conceptItems = {};
        this._osCatalogId = launchOptions.catalogId;
        this._osCatalogName = launchOptions.title || "Unknown";
        this._os = {};

        this.retardActions(true);

        this.whenOsCatalogIsReady = _.memoize(this.whenOsCatalogIsReady);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListenersOnOsCatalog() {
        this._osCatalog.on(
            "NamePropertyUpdated",
            (this._onNamePropertyUpdated = this._onNamePropertyUpdated.bind(this))
        );
        this._osCatalog.on(
            "EntityDeletedFromSystem",
            (this._onOsCatalogRemoved = this._onOsCatalogRemoved.bind(this))
        );
        this._osCatalog.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
    }

    _unregisterListenersOnOsCatalog() {
        this._osCatalog.removeListener("NamePropertyUpdated", this._onNamePropertyUpdated);
        this._osCatalog.removeListener("EntityDeletedFromSystem", this._onOsCatalogRemoved);
        this._osCatalog.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
    }

    _onDirtyStateChanged(newVal) {
        this._tabCmpnt.setDirty(newVal);
    }

    _onNamePropertyUpdated() {
        this.updateTab(this._osCatalog.pristineProperties.name);
    }

    _onOsCatalogRemoved() {
        this.currentEntityRemoved = true;
        if (!this.currentEntityDeleteInProgress) {
            this.currentEntityRemoved = false;
            this.closeEditor(this.editorTabStateId, true);
        }
    }

    getTabCmpnt() {
        return this._tabCmpnt;
    }

    getOsCatalog() {
        return this._osCatalog;
    }

    getOsCatalogId() {
        return this._osCatalogId;
    }

    get activeOrgId() {
        return this._activeOrgId;
    }

    get osCatalogEntity() {
        return this._OsCatalogEntity;
    }

    set conceptItems(newConceptItems) {
        this._conceptItems = _.cloneDeep(newConceptItems);
        return this;
    }

    get conceptItems() {
        return this._conceptItems;
    }

    get userAccessToPlan() {
        return this._userRBAC;
    }

    set os(newOs) {
        this._os = _.cloneDeep(newOs);
        return this;
    }

    get os() {
        return this._os;
    }

    get steveIO() {
        return this._steveIo;
    }

    get editorTabStateId() {
        return this._editorTabState.id;
    }

    get osImagesPathsEntity() {
        return this._getOsImagePathsEntity();
    }

    _getOsImagePathsEntity() {
        return this._OsImagePathsEntity;
    }

    get osImagesEntity() {
        return this._getOsImageEntity();
    }

    _getOsImageEntity() {
        return this._OsImageEntity;
    }

    get deviceEntity() {
        return this._getdeviceEntity();
    }

    _getdeviceEntity() {
        return this._DeviceEntity;
    }
    getUserInfo() {
        return this._userService.get({userId: this._userId});
    }

    whenOsCatalogIsReady() {
        return this._OsCatalogEntity
            .retrieveOsImageCatalog(this.activeOrgId, this._osCatalogId)
            .then((osCatalog) => {
                this._osCatalog = osCatalog;
                this._registerListenersOnOsCatalog();
                return osCatalog;
            })
            .then(() => {
                if (this._userSession.user.authorizedActions.indexOf("osmCatalogWrite") >= 0) {
                    return Promise.resolve(true);
                }
                return Promise.resolve(false);
            })
            .then((results) => {
                if (!results) {
                    this._userRBAC = true;
                }
                return Promise.resolve();
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    updateTab(name) {
        this._tabCmpnt.setTitle(name);
    }

    updateTabIcon(iconName) {
        this._tabCmpnt.setEditorIconName(iconName);
    }

    closeEditor(id, force) {
        id = id || this._editorTabState.id;
        return super.closeEditor(id, force);
    }

    shutdown() {
        let logger = this._logger;
        this.whenOsCatalogIsReady.cache.clear();

        return super
            .shutdown()
            .then(() => {
                if (!_.isUndefined(this._osCatalog)) {
                    this._unregisterListenersOnOsCatalog();
                    return this._osCatalog.shutdown();
                } else {
                    return Promise.resolve();
                }
            })
            .then(() => {
                this._logger = undefined;
                this._editorTabState = undefined;
                this._steveIo = undefined;

                this._tabCmpnt = undefined;
                this._osCatalog = undefined;
                this._registeredDetails = undefined;
                this._editorLayout = undefined;
                this._activeOrgId = undefined;
                this._userService = undefined;
                this._userSession = undefined;
                this._osCatalogId = undefined;
                this._osCatalogName = undefined;
                this._conceptItems = undefined;
                this._os = undefined;
                this._userId = undefined;
                this.currentEntityRemoved = undefined;
                this.currentEntityDeleteInProgress = undefined;
            })
            .catch((err) => {
                logger.warn({error: err}, "OsCatalogEditor.shutdown() error");
            });
    }

    /*******************************************/
    /* END PUBLIC COMPONENT MODEL METHODS      */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    saveJsonFile(fileName, content) {
        this._steveIo.fileFactory.saveJsonAsFile(content, fileName);
        return true;
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = OsCatalogEditor;

OsCatalogEditor.$inject = [
    "$teve.logger",
    "$teve.di",
    "$teve.registeredDetails",
    "editorLayout.editorState",
    "editorLayout.editorLayoutMgr",
    "EditorLayout.EditorLayout",
    "$teve.entities.Device",
    "$teve.entities.OsImageCatalog",
    "$teve.entities.OsImagePaths",
    "$teve.entities.OsImage",
    "$teve.userSession",
    "$teve.services.instance.User",
    "editorLayout.editor.tabComponent",
    "$teve.solutionsManager",
    "$teve.io",
];

//Optional convenient way to set a tab icon.
OsCatalogEditor.$editorIcon = "osmanager.os_catalog";
OsCatalogEditor.$editorIconType = "base-icon-gl";
